/**
 * AgentStatusCard Component
 * AIDLC Administrative Backend
 *
 * Individual agent card displayed in the Agent Dashboard grid.
 * Shows: name, role, status badge, current task, key stats, the agent's open work sessions
 * (one line each: project · task · start time, or "No update since ..."), and last updated timestamp.
 *
 * Bridge Integration:
 *   When an agent's status changes via the bridge, the card shows:
 *   - A pulse animation on the status dot for 10 seconds after update
 *   - "Last updated X seconds ago" timestamp from statusTimestamp
 *
 * Status color mapping aligned with Agent type definition:
 *   working   -> green (actively executing)
 *   available -> blue  (ready for tasks)
 *   waiting   -> yellow (blocked/waiting on input)
 *   away      -> gray  (offline/unavailable)
 */

import React, { useState, useEffect } from 'react';
import type { Agent } from '../../types/agent';
import { formatDurationMs, type SessionSummaryRow } from '../../services/agentSessionService';
import { LinkedCost } from './LinkedCost';
import { projectLabel, formatSessionTime, quietNote, SESSION_STATE_STYLE } from './workSessionFormat';

interface AgentStatusCardProps {
  agent: Agent;
  onClick?: (agentId: string) => void;
  /** Today's session summary for this agent (BL-1bb921de). */
  sessionSummary?: SessionSummaryRow;
}

const statusColors: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  working: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500', label: 'Working' },
  available: { bg: 'bg-blue-50', text: 'text-ldaja-blue', dot: 'bg-ldaja-blue', label: 'Available' },
  waiting: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500', label: 'Waiting' },
  away: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400', label: 'Away' },
  offline: { bg: 'bg-gray-50', text: 'text-gray-400', dot: 'bg-gray-300', label: 'Offline' },
};

function formatTimeAgo(isoTimestamp: string): string {
  const now = Date.now();
  const then = new Date(isoTimestamp).getTime();
  if (isNaN(then)) return '';
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 5) return 'just now';
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  return `${Math.floor(diffSeconds / 86400)}d ago`;
}

function isRecentlyUpdated(isoTimestamp: string): boolean {
  const now = Date.now();
  const then = new Date(isoTimestamp).getTime();
  if (isNaN(then)) return false;
  return (now - then) < 10000; // 10 seconds
}

function formatClock(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function AgentStatusCard({ agent, onClick, sessionSummary }: AgentStatusCardProps) {
  // Parallel runs: the earliest open one says how long the agent has been active.
  const openRuns = sessionSummary?.openSessions ?? [];
  const openSession = openRuns.reduce<(typeof openRuns)[number] | undefined>(
    (earliest, s) => (!earliest || s.startedAt < earliest.startedAt ? s : earliest), undefined);
  const workSessions = agent.activeSessions ?? [];
  const colors = statusColors[agent.status] ?? statusColors.away;
  const activeTask = agent.workspaceTasks?.find((t) => t.status === 'in-progress');
  const [, setTick] = useState(0);

  // Re-render every 5 seconds to keep "X seconds ago" current
  useEffect(() => {
    if (!agent.statusTimestamp) return;
    const interval = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(interval);
  }, [agent.statusTimestamp]);

  const recentlyUpdated = agent.statusTimestamp ? isRecentlyUpdated(agent.statusTimestamp) : false;

  return (
    <div
      className={`bg-white border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer ${
        recentlyUpdated
          ? 'border-green-300 shadow-green-100 shadow-sm'
          : 'border-gray-200 hover:border-ldaja-blue/30'
      }`}
      onClick={() => onClick?.(agent.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(agent.id);
        }
      }}
      aria-label={`Agent ${agent.name}, status ${agent.status}`}
    >
      {/* Top row: Avatar initial + Name + Status */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ backgroundColor: agent.avatarColor || '#2563EB' }}
          >
            {agent.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-ldaja-black">{agent.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{agent.role}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
          {/* Pulse animation on status dot when recently updated */}
          <span className="relative flex h-1.5 w-1.5">
            {recentlyUpdated && (
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${colors.dot} opacity-75`} />
            )}
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${colors.dot}`} />
          </span>
          {colors.label}
        </span>
      </div>

      {/* Current Task or Status Task */}
      {agent.statusTask ? (
        <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
          <span className="text-gray-500">Current: </span>
          <span className="text-gray-700">{agent.statusTask}</span>
        </div>
      ) : activeTask ? (
        <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
          <span className="text-gray-500">Task: </span>
          <span className="text-gray-700">{activeTask.title}</span>
        </div>
      ) : (
        <div className="mb-3 p-2 bg-gray-50 rounded text-xs text-gray-400">
          No active task
        </div>
      )}

      {/* Expertise Tags */}
      {agent.expertise && agent.expertise.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {agent.expertise.slice(0, 3).map((skill) => (
            <span
              key={skill}
              className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded"
            >
              {skill}
            </span>
          ))}
          {agent.expertise.length > 3 && (
            <span className="text-[10px] px-1.5 py-0.5 text-gray-400">
              +{agent.expertise.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Stats Row */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <div>
          <span className="font-medium text-gray-700">
            {agent.statistics.tasksCompleted}
          </span>{' '}
          tasks
        </div>
        <div>
          <span className="font-medium text-gray-700">
            {agent.statistics.totalTokensUsed.toLocaleString()}
          </span>{' '}
          tokens
        </div>
      </div>

      {/* AGENT-STATUS-1: one line per open run, "Project · task · time" */}
      {workSessions.length > 0 && (
        <ul className="mt-2 space-y-1" aria-label={`${agent.name}'s active sessions`}>
          {workSessions.map((s) => {
            const note = quietNote(s);
            return (
              <li key={s.id} className="flex items-start gap-1.5 text-[10px] leading-snug" data-testid="agent-work-session">
                <span className={`mt-1 inline-flex h-1.5 w-1.5 flex-shrink-0 rounded-full ${SESSION_STATE_STYLE[s.status].dot}`} title={SESSION_STATE_STYLE[s.status].label} />
                <span className="min-w-0 text-gray-600">
                  <span className="font-medium text-ldaja-blue">{projectLabel(s)}</span>
                  {' · '}
                  <span className="text-gray-700">{s.task ?? 'No task given'}</span>
                  {' · '}
                  <span className={note ? 'text-amber-700' : 'text-gray-500'}>{note ?? `since ${formatSessionTime(s.startedAt)}`}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Session timestamps (BL-1bb921de) */}
      {(openSession || sessionSummary) && (
        <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-500">
          {openSession && (
            <span className="text-green-700">
              Active since {formatClock(openSession.startedAt)} ({formatDurationMs(Date.now() - new Date(openSession.startedAt).getTime())})
              {openRuns.length > 1 && ` · ${openRuns.length} runs`}
            </span>
          )}
          {sessionSummary && (
            <span className="ml-auto">
              Today: {formatDurationMs(sessionSummary.busyMs)} active,{' '}
              <LinkedCost usd={sessionSummary.costUsd} missingSessions={sessionSummary.costMissingSessions} pendingSessions={sessionSummary.costPendingSessions} estimated={sessionSummary.costEstimated} />
            </span>
          )}
        </div>
      )}

      {/* Last Updated Timestamp */}
      {agent.statusTimestamp && (
        <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400">
          Updated {formatTimeAgo(agent.statusTimestamp)}
        </div>
      )}
    </div>
  );
}
